/** @jsx jsx */
import { jsx, ThemeUIStyleObject } from '@theme-ui/core';
import { Loader } from '@uberall/zenit';

import { Link } from 'src/near-me-check/components/link';

type ButtonProps = {
    to?: string;
    className?: string;
    children: React.ReactNode;
    block?: boolean;
    size?: 'medium' | 'large';
    icon?: React.ReactNode;
    secondary?: boolean;
    type?: 'button' | 'submit';
    disabled?: boolean;
    isLoading?: boolean;
    uppercase?: boolean;
    shouldOpenInSameTab?: boolean;
    onClick?: JSX.IntrinsicElements['button']['onClick'];
};

export function Button({
    to,
    type = 'button',
    onClick,
    block,
    size = 'large', // TODO UB-38450: make "medium" the default version
    children,
    icon,
    secondary,
    disabled,
    isLoading,
    uppercase,
    ...props
}: ButtonProps) {
    const buttonSx = getButtonSx({ block, icon, secondary, uppercase, isLoading });
    const buttonChildren = getButtonChildren({ children, size, icon, isLoading });
    const disableButton = disabled || isLoading;

    return to ? (
        <Link to={to} {...props} sx={buttonSx}>
            {buttonChildren}
        </Link>
    ) : (
        <button type={type} onClick={onClick} disabled={disableButton} {...props} sx={buttonSx}>
            {buttonChildren}
        </button>
    );
}

function getButtonSx({
    block,
    icon,
    secondary,
    uppercase,
    isLoading,
}: Pick<
    ButtonProps,
    'block' | 'icon' | 'secondary' | 'uppercase' | 'isLoading'
>): ThemeUIStyleObject {
    return {
        // Layout
        display: block ? 'block' : 'inline-block',
        width: block ? '100%' : 'auto',
        paddingY: 0,
        borderWidth: icon ? 2 : 1,
        borderRadius: 1,

        // Color
        borderStyle: 'solid',
        borderColor: secondary ? 'nearMeCheck.grey.rich' : 'whitelabel.buttonBackground',
        backgroundColor: secondary ? 'nearMeCheck.white' : 'whitelabel.buttonBackground',
        color: secondary ? 'nearMeCheck.grey.rich' : 'nearMeCheck.white',

        // Font
        fontFamily: 'normal',
        fontSize: 3,
        textAlign: 'center',
        lineHeight: 'condensed',
        fontWeight: secondary ? 'normal' : 'bold',
        letterSpacing: uppercase ? '0.05em' : 'normal',
        textTransform: uppercase ? 'uppercase' : 'none',
        textDecoration: 'none',

        // Other
        transition: 'color .25s ease, background-color .25s ease, border-color .25s ease',
        cursor: 'pointer',
        userSelect: 'none',
        overflow: 'hidden',

        '&:hover, &:focus': {
            backgroundColor: secondary ? 'nearMeCheck.grey.1' : 'whitelabel.buttonHoverBackground',
            borderColor: secondary ? 'nearMeCheck.grey.rich' : 'whitelabel.buttonHoverBackground',
            color: secondary ? 'nearMeCheck.grey.rich' : 'nearMeCheck.white',
        },

        '&:active': {
            backgroundColor: secondary ? 'nearMeCheck.grey.1' : 'whitelabel.buttonActiveBackground',
            borderColor: secondary ? 'nearMeCheck.blue.light' : 'whitelabel.buttonActiveBackground',
            color: secondary ? 'nearMeCheck.grey.rich' : 'nearMeCheck.white',
        },

        // If its a loading state we have two different background colors one for secondary buttons other for primary buttons
        '&:disabled': {
            backgroundColor: isLoading
                ? secondary
                    ? 'grey.7'
                    : 'whitelabel.buttonHoverBackground'
                : 'grey.7',
            borderColor: isLoading
                ? secondary
                    ? 'grey.7'
                    : 'whitelabel.buttonHoverBackground'
                : 'grey.7',
            cursor: 'not-allowed',
        },
    };
}

function getButtonChildren({
    children,
    size,
    icon,
    isLoading,
}: Pick<ButtonProps, 'children' | 'size' | 'icon' | 'isLoading'>) {
    return (
        <div
            sx={{
                display: 'flex',
            }}
        >
            {icon && (
                <div
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'nearMeCheck.white',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}
                >
                    {icon}
                </div>
            )}
            <div
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    paddingY: icon ? '14px' : size === 'large' ? '15px' : '8px',
                    paddingX: 3,
                }}
            >
                {isLoading && <Loader sx={{ marginRight: 2 }} />}
                {children}
            </div>
        </div>
    );
}
